import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
} from "@tanstack/react-table";
import { format } from "date-fns";
import { AlertOctagon, AlertTriangle, CalendarIcon, CheckCircle2, Link2 } from "lucide-react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { trendDomain } from "../../../components/portfolio-risk-concentration/chartScale";
import { JointMembersTable } from "../../../components/portfolio-risk-concentration/JointMembersTable";
import { PageHeader } from "../../../components/portfolio-risk-concentration/PageHeader";
import { StatCard } from "../../../components/portfolio-risk-concentration/StatCard";
import {
  StatusBadge,
  statusClass,
} from "../../../components/portfolio-risk-concentration/StatusBadge";
import { StepShell } from "../../../components/portfolio-risk-concentration/StepShell";
import {
  directionLabel,
  isBreached,
  limitNames,
  pct,
  thr,
  usd,
} from "../../../components/portfolio-risk-concentration/format";
import {
  SortableTableHead,
  directionSortingFn,
  statusSortingFn,
  trendDelta,
  trendSortingFn,
} from "../../../components/portfolio-risk-concentration/SortableTableHead";
import { Alert, AlertDescription, AlertTitle } from "../../../ui/alert";
import { Button } from "../../../ui/button";
import { Calendar } from "../../../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../../ui/popover";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../../ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import {
  FILTER_TRIGGER,
  FilterField,
  INVESTOR_LABEL,
  PrcFilterBar,
  SCOPE_DESCRIPTION,
  FilterSelect,
  VALUE_BASIS_FILTERS,
  useInvestorFilterOptions,
  usePrcFilters,
  type Investor,
} from "../../../components/portfolio-risk-concentration/PrcFilterBar";
import { useBookLabels } from "../../../utils/portfolio-risk-concentration/api/facilities";
import { MonitorTotalCards } from "../../../components/portfolio-risk-concentration/PortfolioTotalCards";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import { useConcentration } from "../../../utils/portfolio-risk-concentration/api/concentration";
import type {
  ConcentrationRow,
  LimitBasis,
} from "../../../utils/portfolio-risk-concentration/api/types";
import { cn } from "../../../utils/common/cn";

// severityRank / sortBySeverity / pct / usd / DIRECTION_LABEL / statusClass / StatusBadge
// now live in components/portfolio-risk-concentration (format.ts and StatusBadge.tsx).
// They were duplicated verbatim here and in CapacityPage; the distance-to-threshold
// division inside sortBySeverity needed a divide-by-zero guard, and one definition means
// one place to read it.

function Sparkline({ row }: { row: ConcentrationRow }) {
  const data = row.trend.map((p) => ({ x: p.date, y: p.dollarPct }));
  // An unmeasured limit has an empty series. Recharts renders that without error, but it
  // would still draw the dashed threshold line — a chart with an axis and no data reads as
  // "flat at zero" rather than "nothing to plot". Say nothing instead.
  if (data.length === 0) {
    return <span className="text-muted-foreground">—</span>;
  }
  const color =
    row.status === "RED"
      ? "hsl(var(--destructive))"
      : row.status === "AMBER"
        ? "var(--amber)"
        : "var(--approve)";
  return (
    <div className="h-9 w-28">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 4, bottom: 4, left: 0, right: 0 }}>
          <YAxis hide domain={trendDomain(row.thresholdPct)} />
          <ReferenceLine y={row.thresholdPct} stroke="hsl(var(--muted-foreground))" strokeDasharray="3 3" />
          <Line type="monotone" dataKey="y" stroke={color} strokeWidth={1.5} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function MonitorPage() {
  const bookLabel = useBookLabels();
  const { investorsWithAll } = useInvestorFilterOptions();
  const [searchParams, setSearchParams] = useSearchParams();

  const limitParam = searchParams.get("limit") ?? undefined;
  // Scope, book and investor all come from usePrcFilters, so this page cannot disagree with
  // Capacity / Snapshot / Manage limits about what a filter change means. In particular a
  // scope toggle no longer deletes `?book=` — see the hook's docstring.
  const [openId, setOpenId] = useState<string | null>(null);
  const filters = usePrcFilters(searchParams, setSearchParams, () => {
    // Changing the filters changes which rows exist, and the open row belongs to the
    // selection being left.
    setOpenId(null);
  });
  const { scope, facility, product, investor, isExternal } = filters;
  const basis: LimitBasis = searchParams.get("basis") === "proposed" ? "proposed" : "approved";
  const isProposed = basis === "proposed";
  const [asOf, setAsOf] = useState<Date>(() => new Date());
  const asOfDate = format(asOf, "yyyy-MM-dd");

  // The book is pushed into the request rather than filtered client-side: /concentration has
  // always served one book on facility=CAP1|CAP2, and its notes[] and denominators are
  // book-specific, so filtering the EXTERNAL response would leave prose describing rows that
  // are no longer shown.
  // include_joints=1: joint rows come back in the same ConcentrationRow shape, so the
  // table below needs no new concept — they sort, badge and trend like any other row.
  const { data } = useConcentration(
    filters.concentrationFacility,
    asOfDate,
    true,
    basis,
    investor,
    facility,
    product,
    filters.valueBasis,
  );
  // NOT pre-sorted. Ordering belongs to the table so the header controls actually own it
  // — pre-sorting here would fight whatever the reader clicked. The default (Category,
  // ascending) is set in the table's initialState below.
  const rows = useMemo(() => data?.rows ?? [], [data]);

  // NO facility-capacity cards on Monitor. Commitment/Drawn/Remaining (external) and
  // Capital base/Allocated/Unallocated (internal) used to sit above this table; they are
  // Capacity's subject, not Monitor's. Monitor answers "is any limit near breach", and a
  // facility-level ruler above a per-limit table invited the two to be read as one
  // reconciliation, which they are not — limits overlap the same dollars rather than
  // partitioning the facility. The cards live on Capacity, which is one click away.

  useEffect(() => {
    if (limitParam) setOpenId(limitParam);
  }, [limitParam]);

  const selected = rows.find((r) => r.limitId === openId) ?? null;
  const scopeName = SCOPE_DESCRIPTION[scope];

  /** Writes `basis`, the one control on this page that is not part of the shared filter
   *  set. It re-scores the rows rather than changing which exist, so the detail sheet can
   *  stay open across it — unlike the filters, which usePrcFilters closes it for. */
  const setParams = (mutate: (next: URLSearchParams) => void) => {
    const next = new URLSearchParams(searchParams);
    mutate(next);
    setSearchParams(next);
  };

  const columns = useMemo<ColumnDef<ConcentrationRow>[]>(
    () => [
      {
        accessorKey: "limitDesc",
        header: "Limit",
        // Joint rows are marked, not separated: same table, same columns. The category
        // cell already renders "Joint" on its own, so this stays deliberately subtle.
        cell: ({ row }) => (
          <span className="flex items-start gap-1.5 font-medium">
            {row.original.isJoint && (
              <Link2 className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" />
            )}
            <span>
              {row.original.limitDesc}
              {row.original.isJoint && (
                <span className="ml-1.5 text-[11px] font-normal text-muted-foreground">
                  · {row.original.memberCount ?? row.original.memberRefs?.length ?? 0} limits
                </span>
              )}
            </span>
          </span>
        ),
      },
      // Only on the External scope, where CAP 1 and CAP 2 rows sit in one table.
      // Each is measured against its OWN book's total net investment, so the column
      // is not decoration — without it two rows with the same limit description and
      // different percentages look like a bug.
      ...(isExternal
        ? [
            {
              id: "book",
              header: "Book",
              accessorFn: (r) => bookLabel[r.facilityCode] ?? r.facilityCode,
              cell: ({ row }) => (
                <span className="whitespace-nowrap text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                  {bookLabel[row.original.facilityCode] ?? row.original.facilityCode}
                </span>
              ),
            } as ColumnDef<ConcentrationRow>,
          ]
        : []),
      // Only under "All investors", where the table holds two investors' limits scored
      // against different capital bases. Naming the frame is what keeps the Current column
      // honest — without it, 0.63% of Wafra's $2.5B sorts against 4.10% of Navitas' $385M
      // as though they were the same measurement.
      ...(investor === "ALL"
        ? [
            {
              id: "investor",
              accessorFn: (r: ConcentrationRow) => r.investor ?? "",
              header: "Investor",
              cell: ({ row }: { row: { original: ConcentrationRow } }) => (
                <span className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                  {INVESTOR_LABEL[(row.original.investor ?? "") as Investor] ??
                    row.original.investor ??
                    "—"}
                </span>
              ),
            } as ColumnDef<ConcentrationRow>,
          ]
        : []),
      {
        accessorKey: "categoryGroup",
        header: "Category",
        cell: ({ row }) => (
          <span className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
            {row.original.categoryGroup}
          </span>
        ),
      },
      {
        accessorKey: "currentDollarPct",
        header: "Current",
        cell: ({ row }) => (
          <span className="tabular-nums">
            <span className={cn("font-medium", statusClass(row.original.status))}>
              {pct(row.original.currentDollarPct)}
            </span>
            {/* Deliberately bare. A joint reports its GOVERNING member's own percentage
                against that member's own threshold, so the pair reads exactly like a single
                limit's and needs no qualifier. Which member governs, and whether another is
                closer to its own threshold, are both in the detail sheet's member table. */}
          </span>
        ),
      },
      // Sits immediately after Current, next to the number it is judged against. Without it
      // the table showed a percentage and a RAG badge and left the reader to open the detail
      // sheet to find out how close the row actually was.
      {
        accessorKey: "thresholdPct",
        header: "Threshold",
        cell: ({ row }) => {
          const r = row.original;
          // A joint's threshold is now the LOWEST of its members' -- the joint is governed
          // by its tightest limit -- so it renders like any single limit and the
          // "100% / utilisation" special case is gone. What the cell adds is the warning the
          // old basis used to carry implicitly: if some OTHER member is closer to its own
          // threshold than the governing one, this row's badge understates the joint, and
          // that has to be visible without opening the detail sheet.
          if (r.isJoint) {
            return <span className="tabular-nums">{thr(r.thresholdPct)}</span>;
          }
          return (
            <span className="tabular-nums">
              {r.approvedThresholdPct !== undefined ? (
                <>
                  <span className="text-muted-foreground line-through">
                    {thr(r.approvedThresholdPct)}
                  </span>{" "}
                  <span className="font-medium text-amber">{thr(r.thresholdPct)}</span>
                </>
              ) : (
                thr(r.thresholdPct)
              )}
              {r.capitalCapDollars !== null && (
                <span className="block text-[11px] text-muted-foreground">
                  cap {usd(r.capitalCapDollars)}
                </span>
              )}
            </span>
          );
        },
      },
      {
        id: "trend",
        header: "Trend",
        accessorFn: trendDelta,
        cell: ({ row }) => <Sparkline row={row.original} />,
        // A sparkline has no value to sort on; this ranks by how far the series moved.
        sortingFn: trendSortingFn,
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <StatusBadge
            status={row.original.status}
            title={row.original.unmeasuredReason}
          />
        ),
        // RED -> AMBER -> GREEN ascending, not the alphabetical order a string sort gives.
        sortingFn: statusSortingFn,
      },
      {
        accessorKey: "direction",
        header: "Direction",
        cell: ({ row }) => (
          // Coloured only when breached: the other three values are neutral facts about a
          // trend line, and giving them all a colour would compete with the Status badge.
          <span
            className={cn(
              "text-xs",
              isBreached(row.original)
                ? "font-medium text-destructive"
                : "text-muted-foreground",
            )}
          >
            {directionLabel(row.original)}
          </span>
        ),
        sortingFn: directionSortingFn,
      },
    ],
    // investor is a dep because the Investor column appears only under "All".
    [isExternal, investor, bookLabel],
  );

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    // Category first, then the limit's own name inside it — a stable, readable default that
    // groups a book's Equipment limits together instead of scattering them by severity.
    // Every header re-sorts from here; nothing is locked.
    initialState: {
      sorting: [
        { id: "categoryGroup", desc: false },
        { id: "limitDesc", desc: false },
      ],
    },
  });

  // The banner's two sets, derived here so the title and the description cannot name
  // different ones. `flaggedRows` is every non-GREEN row and drives the AMBER banner;
  // `redRows` drives the RED one. The RED banner used to count these and then list
  // `aggregateFlag.flaggedLimits`, which is the amber set too — so it claimed four limits
  // and named seven. aggregateFlag is still what decides whether the amber banner FIRES
  // (three or more non-GREEN, a backend rule), but the names now come from the rows on
  // screen, which are the ones the reader can go and check.
  const redRows = rows.filter((r) => r.status === "RED");
  const flaggedRows = rows.filter((r) => r.status !== "GREEN");
  const anyRed = redRows.length > 0;
  const flagged = data?.aggregateFlag;

  return (
    <StepShell step={1}>
      <PageHeader
        title="Monitor"
      />

      <div className="mt-5">
        <PrcFilterBar
          scope={scope}
          onScopeChange={filters.setScope}
          facility={facility}
          onFacilityChange={filters.setFacility}
          product={product}
          onProductChange={filters.setProduct}
          investor={investor}
          onInvestorChange={filters.setInvestor}
          investorOptions={investorsWithAll}
          right={
            <div className="flex items-end gap-3">
            {/* Total value (on the "Measured in" basis) and contract count of the book the filters name. Facility is inert under Internal,
                which measures the investor's whole book. */}
            <MonitorTotalCards
              investor={investor}
              facility={scope === "INTERNAL" ? "ALL" : facility}
              product={product}
              valueBasis={filters.valueBasis}
              asOfDate={asOfDate}
            />
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="h-9 gap-2">
                  <CalendarIcon className="h-3.5 w-3.5" />
                  As of <span suppressHydrationWarning>{format(asOf, "d MMM yyyy")}</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={asOf}
                  onSelect={(d) => d && setAsOf(d)}
                  defaultMonth={asOf}
                  captionLayout="dropdown-buttons"
                  fromYear={2015}
                  toYear={new Date().getFullYear()}
                  disabled={{ after: new Date() }}
                />
              </PopoverContent>
            </Popover>
            </div>
          }
        >
          {/* Which quantity the percentages are taken in. Sits beside Basis deliberately:
              the two read alike in English and mean entirely different things, so seeing
              them together is the clearest way to keep them apart. This one changes what
              is measured; Basis below changes which thresholds it is measured against. */}
          <FilterSelect
            label="Measured in"
            value={filters.valueBasis}
            options={VALUE_BASIS_FILTERS}
            onChange={filters.setValueBasis}
          />

          {/* Basis is not a filter — it does not narrow the rows, it re-scores them. Built
              from FilterField + FILTER_TRIGGER rather than its own markup so it cannot drift
              from the controls beside it; it carried a hardcoded w-[220px] and would have
              been the one control left at the old width. */}
          <FilterField label="Basis">
            <Select
              value={basis}
              onValueChange={(v) => setParams((next) => next.set("basis", v))}
            >
              <SelectTrigger className={FILTER_TRIGGER} aria-label="Basis">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="approved">Approved Limits</SelectItem>
                <SelectItem value="proposed">Proposed Limits</SelectItem>
              </SelectContent>
            </Select>
          </FilterField>
        </PrcFilterBar>
      </div>


      {/* An empty result is a real answer, not a failure — selecting an investor with no
          external facility is legal and lands here. Say what happened and why, using the
          note the API sent, rather than letting the status alert below report "all limits
          within agreed thresholds" about a table with nothing in it. */}
      {data && rows.length === 0 && (
        <Alert className="mt-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>No limits in this selection</AlertTitle>
          <AlertDescription>
            {data.notes?.[0] ??
              "Nothing matches the current filters. Try a different scope or facility."}
          </AlertDescription>
        </Alert>
      )}

      {data && rows.length > 0 && (
        <Alert
          className={cn(
            "mt-4",
            anyRed
              ? "border-destructive/50 bg-destructive/10"
              : flagged?.fired
                ? "border-amber/50 bg-amber-dim/40"
                : "border-[color:var(--approve)]/40 bg-[color:var(--approve)]/10",
          )}
        >
          {anyRed ? (
            <AlertOctagon className="h-4 w-4 text-destructive" />
          ) : flagged?.fired ? (
            <AlertTriangle className="h-4 w-4 text-amber" />
          ) : (
            <CheckCircle2 className="h-4 w-4 text-[color:var(--approve)]" />
          )}
          <AlertTitle>
            {anyRed
              ? `${redRows.length} limit(s) at or near breach`
              : flagged?.fired
                ? `${flaggedRows.length} limits approaching their thresholds`
                : "All limits within agreed thresholds"}
          </AlertTitle>
          <AlertDescription>
            {/* Named from the SAME set the title counted. Under the red banner that is the
                red rows only — an amber limit is not at or near breach, and listing it
                there was what made the count and the list disagree. */}
            {anyRed
              ? `${scopeName} as of ${format(asOf, "d MMM yyyy")}: ${limitNames(redRows, bookLabel)}.`
              : flaggedRows.length > 0
                ? `${scopeName} as of ${format(asOf, "d MMM yyyy")}: ${limitNames(flaggedRows, bookLabel)}.`
                : `${scopeName} as of ${format(asOf, "d MMM yyyy")}: no limit is within 10% of its threshold.`}
          </AlertDescription>
        </Alert>
      )}

      <div className="mt-4 overflow-hidden rounded-lg border border-border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((h) => (
                  <SortableTableHead key={h.id} header={h} />
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((r) => (
              <TableRow
                key={r.id}
                onClick={() => setOpenId(r.original.limitId)}
                className="cursor-pointer"
              >
                {r.getVisibleCells().map((c) => (
                  <TableCell key={c.id}>
                    {flexRender(c.column.columnDef.cell, c.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setOpenId(null)}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-lg">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2 pr-6">
                  <span>{selected.limitDesc}</span>
                  <StatusBadge
                    status={selected.status}
                    title={selected.unmeasuredReason}
                  />
                </SheetTitle>
                <SheetDescription>
                  {/* Names the row's OWN book, not the scope: on External the table
                      holds both, and "for External books" would hide which total the
                      percentage below is taken against. */}
                  {selected.isJoint ? (
                    <>
                      Joint of {selected.memberCount ?? selected.memberRefs?.length ?? 0} limits on{" "}
                      {bookLabel[selected.facilityCode] ?? selected.facilityCode}. Governed by{" "}
                      <strong>{selected.governingLimitDesc ?? selected.bindingLimitDesc}</strong>,
                      its lowest threshold at {thr(selected.thresholdPct)} — every figure below is
                      that limit's. The dashed line marks its threshold.
                      {selected.worstMemberLimitDesc && (
                        <>
                          {" "}
                          Note <strong>{selected.worstMemberLimitDesc}</strong> is closer to its
                          own threshold; see the member table.
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      {selected.categoryGroup} concentration for{" "}
                      {bookLabel[selected.facilityCode] ?? selected.facilityCode}. Dashed line
                      marks the current threshold ({thr(selected.thresholdPct)}).
                    </>
                  )}
                </SheetDescription>
              </SheetHeader>

              <div className="space-y-4 px-4 pb-6">
                {selected.isJoint && <JointMembersTable row={selected} />}
                <div className="h-[220px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={selected.trend.map((p) => ({ x: p.date, y: p.dollarPct }))}
                      margin={{ top: 8, right: 8, bottom: 0, left: -12 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="x" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                      {/* Domain, not the default: a bare YAxis scales to the series and
                          clips the threshold line off the top — see trendDomain. */}
                      <YAxis tick={{ fontSize: 10 }} domain={trendDomain(selected.thresholdPct)} />
                      <Tooltip
                        formatter={(v: number) => pct(Number(v))}
                        contentStyle={{ backgroundColor: "#fff", border: "1px solid #ccc" }}
                        labelStyle={{ color: "#000" }}
                        itemStyle={{ color: "#000" }}
                      />
                      <ReferenceLine
                        y={selected.thresholdPct}
                        stroke="hsl(var(--destructive))"
                        strokeDasharray="4 4"
                      />
                      <Line
                        type="monotone"
                        dataKey="y"
                        stroke="hsl(var(--primary))"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <StatCard
                    label="Current"
                    value={pct(selected.currentDollarPct)}
                    sublabel={
                      selected.isJoint
                        ? `on ${selected.governingLimitDesc ?? selected.bindingLimitDesc}`
                        : undefined
                    }
                    accent={
                      selected.status === "RED"
                        ? "red"
                        : selected.status === "AMBER"
                          ? "amber"
                          : "default"
                    }
                  />
                  <StatCard
                    label="Threshold"
                    value={thr(selected.thresholdPct)}
                    sublabel={
                      selected.approvedThresholdPct !== undefined
                        ? `proposed — approved is ${thr(selected.approvedThresholdPct)}`
                        : selected.isJoint
                          ? `lowest of ${selected.memberCount} member limits`
                          : undefined
                    }
                    accent={selected.approvedThresholdPct !== undefined ? "amber" : "default"}
                  />
                  {/* Was Headroom, which is the Capacity page's entire subject and said
                      nothing new here. Average deal size answers the question actually asked
                      of a concentration — is this one large exposure or many small ones —
                      which the percentage alone cannot distinguish. A breach's cure amount is
                      still in the row's description below and on Capacity. */}
                  <StatCard
                    label="Avg deal size"
                    value={
                      selected.avgDealSizeDollars == null
                        ? "—"
                        : usd(selected.avgDealSizeDollars)
                    }
                    sublabel={
                      selected.avgDealSizeDollars == null
                        ? "no contract count for this limit"
                        : selected.isJoint
                          ? `binding member: ${selected.bindingLimitDesc}`
                          : `across ${(selected.currentCount ?? 0).toLocaleString()} contracts`
                    }
                  />
                  <StatCard
                    label="By count"
                    value={
                      selected.currentCountPct == null
                        ? (selected.currentCount ?? 0).toLocaleString()
                        : pct(selected.currentCountPct)
                    }
                    sublabel={
                      selected.currentCount == null
                        ? "Count basis — secondary to dollar concentration"
                        : selected.populationCount
                          ? `${selected.currentCount.toLocaleString()} of ${selected.populationCount.toLocaleString()} contracts`
                          : `${selected.currentCount.toLocaleString()} contracts`
                    }
                  />
                  {selected.capitalCapDollars !== null && (
                    <StatCard label="Capital cap" value={usd(selected.capitalCapDollars)} />
                  )}
                </div>

                <Button asChild className="w-full">
                  {selected.isJoint ? (
                    <Link
                      to={`/risk-concentrations/manage-limits?${new URLSearchParams({
                        facility: selected.facilityCode === "INTERNAL" ? "internal" : "external",
                        view: "approved",
                        jointId: selected.limitId,
                        from: "monitor",
                      }).toString()}`}
                    >
                      Adjust this joint
                    </Link>
                  ) : (
                    <Link
                      to={`/risk-concentrations/manage-limits?${new URLSearchParams({
                        // Was hardcoded "external", which was right while Monitor only
                        // ever showed external books. Now that it also serves the
                        // internal scope, an internal limit would have landed on the
                        // external side of Manage limits and not been in the list.
                        facility:
                          selected.facilityCode === "INTERNAL" ? "internal" : "external",
                        limitId: selected.limitId,
                        from: "monitor",
                      }).toString()}`}
                    >
                      Adjust this limit
                    </Link>
                  )}
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </StepShell>
  );
}
